<script setup>
import { load } from "cheerio";

const parseHTMLString = (htmlString) => {
	const $ = load(htmlString);
	return $("body").text() || "";
};

const $config = useRuntimeConfig();
const route = useRoute();

const { data } = await useAsyncData(`author-biography`, async ({ $router }) => {
	try {
		let author = await fetchAuthorByName(
			"https://content.timothysykes.com/",
			$router.currentRoute._rawValue.params.authorname[0]
		);

		let schema = {
			"@context": "https://schema.org",
			"@type": "Person",
			name: parseHTMLString(author.name),
			alternateName: "",
			url:
				"https://www.timothysykes.com/blog/author/" +
				$router.currentRoute._rawValue.params.authorname[0] +
				"/",
			mainEntityOfPage: {
				"@type": "WebPage",
				"@id":
					"https://www.timothysykes.com/blog/author/" +
					$router.currentRoute._rawValue.params.authorname[0] +
					"/"
			},
			description: parseHTMLString(author.highlights),
			jobTitle: parseHTMLString(author.position),
			worksFor: {
				"@type": "Organization",
				name: "Timothy Sykes",
				sameAs: "https://www.timothysykes.com"
			},
			image: [
				{
					"@type": "ImageObject",
					url: author.profile_image,
					width: 306,
					height: 384
				}
			],
			sameAs:
				(author.custom_sections &&
					JSON.parse(author.custom_sections)
						.find((section) => section.name === "Social")
						?.values.map((social) => social.value)) ||
				[],
			knowsAbout: [
				{ "@type": "Thing", name: "Penny Stocks" },
				{ "@type": "Thing", name: "Day Trading" },
				{ "@type": "Thing", name: "Investment Strategies" }
			],
			birthDate: "",

			contactPoint: {
				"@type": "ContactPoint",
				contactType: "customer service",
				email: "support-alerts@timothysykes.com",
				telephone: "+1-888-878-3621"
			},
			publishingPrinciples: "https://www.timothysykes.com/editorial-guidelines/"
		};

		if (author.name === "Tim Sykes") {
			const award = ["Timothy Sykes Day Trading Award for the Talented"];
			schema = {
				...schema,
				name: "Timothy Sykes",
				alternateName: "Tim Sykes",
				award,
				sameAs: [
					"https://en.wikipedia.org/wiki/Timothy_Sykes",
					"https://profit.ly/user/timothysykes",
					"https://www.facebook.com/timsykesfans/",
					"https://twitter.com/timothysykes",
					"https://www.linkedin.com/in/timothysykes/",
					"https://www.instagram.com/timothysykes/",
					"https://www.tiktok.com/@timothysykes",
					"https://www.imdb.com/name/nm2418506/",
					"https://observer.com/author/timothy-sykes/",
					"https://books.apple.com/us/author/timothy-sykes/id281184034",
					"https://www.huffpost.com/author/tim-501",
					"https://fortune.com/author/timothy-sykes/",
					"https://www.entrepreneur.com/author/timothy-sykes",
					"https://www.goodreads.com/author/list/748145.Timothy_Sykes",
					"https://www.amazon.com/stores/author/B002BLQ4YC",
					"https://www.businessinsider.com/author/timothy-sykes",
					"https://www.theguardian.com/profile/timothy-sykes"
				]
			};
		}

		author = {
			...author,
			description: parseHTMLString(author.highlights) ?? ""
		};
		return { author, schema };
	} catch (error) {
		console.log(error);
	}
});

const json_schema = computed(() => {
	if (data.value) {
		return JSON.stringify(data.value.schema);
	}

	return "";
});

useHead(() => {
	const scripts = [];

	if (route.params.authorname[0] === "tim-sykes") {
		scripts.push({
			type: "application/ld+json",
			class: "saswp-schema-markup-output",
			innerHTML: JSON.stringify({
				"@context": "https://schema.org",
				"@type": "Organization",
				name: "Timothy Sykes",
				url: "https://www.timothysykes.com",
				description: "How To Trade Penny Stocks Without The Stock Promoter BS",
				disambiguatingDescription:
					"A leading provider of investment education and resources, founded in 2005.",
				foundingDate: "2005-01-15", // Boilerplate text
				logo: {
					"@type": "ImageObject",
					url: $config.public.hostname + "images/ts-logo-icon.png",
					width: 512,
					height: 512
				},
				image: {
					"@type": "ImageObject",
					url: $config.public.hostname + "images/ts-logo-white-bg.png",
					width: 503,
					height: 95
				},
				sameAs: [
					"https://en.wikipedia.org/wiki/Timothy_Sykes",
					"https://www.youtube.com/@TimothySykesTrader",
					"https://www.facebook.com/timsykesfans/",
					"https://twitter.com/timothysykes",
					"https://www.linkedin.com/in/timothysykes/",
					"https://www.instagram.com/timothysykes/",
					"https://www.tiktok.com/@timothysykes",
					"https://profit.ly/user/timothysykes"
				],
				contactPoint: [
					{
						"@type": "ContactPoint",
						contactType: "customer service",
						telephone: "+1-888-878-3621",
						email: "support-alerts@timothysykes.com",
						areaServed: "US",
						availableLanguage: ["English", "Spanish"]
					},
					{
						"@type": "ContactPoint",
						contactType: "media inquiries",
						telephone: "+1-800-878-3621",
						email: "press@timothysykes.com",
						areaServed: "US",
						availableLanguage: ["English"]
					}
				],
				address: {
					"@type": "PostalAddress",
					streetAddress: "66 W. Flagler St. - Suite 900",
					addressLocality: "Miami",
					addressRegion: "FL",
					postalCode: "33130",
					addressCountry: "US"
				},
				founder: {
					"@type": "Person",
					name: "Timothy Sykes",
					url: "https://www.timothysykes.com/blog/author/tim-sykes/",
					sameAs: [
						"https://www.youtube.com/@TimothySykesTrader",
						"https://en.wikipedia.org/wiki/Timothy_Sykes",
						"https://profit.ly/user/timothysykes",
						"https://www.facebook.com/timsykesfans/",
						"https://twitter.com/timothysykes",
						"https://www.linkedin.com/in/timothysykes/",
						"https://www.instagram.com/timothysykes/",
						"https://www.tiktok.com/@timothysykes",
						"https://www.imdb.com/name/nm2418506/",
						"https://observer.com/author/timothy-sykes/",
						"https://books.apple.com/us/author/timothy-sykes/id281184034",
						"https://www.huffpost.com/author/tim-501",
						"https://fortune.com/author/timothy-sykes/",
						"https://www.entrepreneur.com/author/timothy-sykes",
						"https://www.goodreads.com/author/list/748145.Timothy_Sykes",
						"https://www.amazon.com/stores/author/B002BLQ4YC",
						"https://www.businessinsider.com/author/timothy-sykes",
						"https://www.theguardian.com/profile/timothy-sykes"
					],
					image: [
						{
							"@type": "ImageObject",
							url: "https://content.timothysykes.com/wp-content/uploads/2024/08/timsykes.webp",
							width: 306,
							height: 384
						},
						{
							"@type": "ImageObject",
							url: "https://secure.gravatar.com/avatar/092522e87ab76a4bb688d6bfdc0b4f2e?s=96&d=mm&r=g",
							height: 96,
							width: 96
						}
					]
				},
				award: ["Timothy Sykes Day Trading Award for the Talented"],
				publishingPrinciples: "https://www.timothysykes.com/editorial-guidelines/"
			})
		});
	}

	return {
		script: [
			...scripts,
			{
				type: "application/ld+json",
				class: "saswp-schema-markup-output",
				innerHTML: json_schema
			}
		],

		title: "Timothy Sykes | " + data.value?.author.name,
		meta: [{ name: "description", content: data.value?.author.description || "" }]
	};
});
</script>

<template>
	<AuthorsComponent
		v-if="data && data.author"
		:author-name="data.author.name"
		:author-title="data.author.position"
		:author-expertise="data.author.expertise"
		:author-education="data.author.education"
		:author-highlights="data.author.highlights"
		:experience="data.author.experience"
		:author-education-content="data.author.educationContent"
		:author-email="data.author.contact_email"
		:author-image="data.author.profile_image"
		:custom_sections="data.author.custom_sections"
		:any-object="data.author"
	/>
</template>
